import { normalizeSlug } from '~/utils/slug'

export interface CategoryTreeNode {
  id: string
  terms: Record<string, string>
  description?: Record<string, string>
  children: CategoryTreeNode[]
}

export interface ResolvedCategory {
  node: CategoryTreeNode
  ancestors: CategoryTreeNode[]
  children: CategoryTreeNode[]
  depth: number
}

function decodeId(encoded: string): number {
  return Number.parseInt(atob(encoded), 10)
}

function findDeepestMatching(
  nodes: CategoryTreeNode[],
  ancestors: CategoryTreeNode[],
  matches: (node: CategoryTreeNode) => boolean,
): ResolvedCategory | null {
  let best: ResolvedCategory | null = null

  for (const node of nodes) {
    if (!matches(node)) continue

    const current: ResolvedCategory = {
      node,
      ancestors: [...ancestors],
      children: node.children,
      depth: ancestors.length + 1,
    }

    const childResult = findDeepestMatching(node.children, [...ancestors, node], matches)
    const candidate = childResult ?? current

    if (!best || candidate.depth > best.depth) {
      best = candidate
    }
  }

  return best
}

function walkTree(
  roots: CategoryTreeNode[],
  keys: string[],
  match: (node: CategoryTreeNode, key: string) => boolean,
): ResolvedCategory | null {
  let currentNodes = roots
  const ancestors: CategoryTreeNode[] = []

  for (const key of keys) {
    const found = currentNodes.find(node => match(node, key))
    if (!found) return null

    ancestors.push(found)
    currentNodes = found.children
  }

  if (ancestors.length === 0) return null

  const node = ancestors.pop()!
  return {
    node,
    ancestors,
    children: node.children,
    depth: ancestors.length + 1,
  }
}

export const useCategoryTreeStore = defineStore('category-tree', () => {
  const userStore = useUserStore()
  const { baseURL } = useRuntimeConfig().app
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()

  const accessToken = computed(() => userStore.accessToken)
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))

  const {
    data: treeData,
    pending: isLoading,
    refresh: refreshTree,
    error: fetchError,
  } = useFetch<CategoryTreeNode[]>('/api/intern/categories', {
    headers: headerFields,
    baseURL,
    watch: [accessToken],
    ...abortIfNotAuthenticated,
    onResponseError({ response }) {
      // Fire-and-forget: refreshing the token flips `accessToken`, and the
      // `watch: [accessToken]` above performs the single recovery refetch.
      if (response.status === 401) void userStore.reloadAuth()
    },
  })

  const tree = computed(() => treeData.value ?? [])
  const error = computed(() => fetchError.value?.message ?? null)

  const roots = computed(() =>
    [...tree.value].sort((a, b) => decodeId(a.id) - decodeId(b.id)),
  )

  function resolveSlugPath(slugs: string[], locale: string): ResolvedCategory | null {
    return walkTree(tree.value, slugs, (node, slug) =>
      node.terms[locale] != null && normalizeSlug(node.terms[locale]) === slug,
    ) ?? walkTree(tree.value, slugs, (node, slug) =>
      Object.values(node.terms).some(term => normalizeSlug(term) === slug),
    )
  }

  function resolveByTerms(terms: string[], searchLanguage: string): ResolvedCategory | null {
    return walkTree(tree.value, terms, (node, term) =>
      node.terms[searchLanguage] === term,
    )
  }

  function resolveDeepestPath(termsBag: string[], searchLanguage: string): ResolvedCategory | null {
    const termSet = new Set(termsBag)
    return findDeepestMatching(
      tree.value,
      [],
      node => termSet.has(node.terms[searchLanguage] ?? ''),
    )
  }

  function resolveDeepestByIds(ids: string[]): ResolvedCategory | null {
    const idSet = new Set(
      ids.map(Number).filter(n => Number.isInteger(n) && n > 0),
    )
    return findDeepestMatching(
      tree.value,
      [],
      node => idSet.has(decodeId(node.id)),
    )
  }

  function $reset() {
    refreshTree()
  }

  return {
    tree,
    isLoading,
    error,
    roots,
    refreshTree,
    resolveSlugPath,
    resolveByTerms,
    resolveDeepestPath,
    resolveDeepestByIds,
    normalize: normalizeSlug,
    $reset,
  }
})
